/** Monthly screening funnel: grouped bars of screened / screen fails / enrolled by month. */
import { useCallback } from "react";
import * as d3 from "d3";
import {
  COLORS,
  useD3Chart,
  drawBandAxes,
  monthKey,
  topRoundedRectPath,
  FONT,
  type Margin,
} from "./chartBase";
import type {
  MonthlyFunnelChartProps,
  OnboardingMonthRow,
} from "./onboardingPlots.types";

const SERIES: {
  key: keyof OnboardingMonthRow;
  color: string;
  label: string;
}[] = [
  { key: "screenedPerMonth", color: COLORS.screenedBar, label: "Screened" },
  { key: "screenFailsPerMonth", color: COLORS.red, label: "Screen Fails" },
  { key: "enrolledPerMonth", color: COLORS.actual, label: "Enrolled" },
];

export default function MonthlyFunnelChart({
  rows,
  height = 300,
}: MonthlyFunnelChartProps) {
  const draw = useCallback(
    (svg, tip, W) => {
      const H = height;
      const m: Margin = { top: 16, right: 16, bottom: 42, left: 34 };
      const d = rows
        .filter((r) => r.screenedPerMonth != null)
        .map((r) => ({ ...r, mm: monthKey(r.date) }));
      if (!d.length) return;

      const x0 = d3
        .scaleBand()
        .domain(d.map((r) => r.mm))
        .range([m.left, W - m.right])
        .padding(0.25);
      const x1 = d3
        .scaleBand()
        .domain(SERIES.map((s) => s.key as string))
        .range([0, x0.bandwidth()])
        .padding(0.08);
      const maxSeriesValue =
        d3.max(d, (r) =>
          d3.max(
            SERIES.map((s) => r[s.key] as number | null).filter(
              (v): v is number => v != null,
            ),
          ),
        ) ?? 1;
      const y = d3
        .scaleLinear()
        .domain([0, maxSeriesValue])
        .nice()
        .range([H - m.bottom, m.top]);

      drawBandAxes(svg, W, H, m, x0, y);

      svg
        .selectAll("g.grp")
        .data(d)
        .join("g")
        .attr("transform", (r: any) => `translate(${x0(r.mm)},0)`)
        .selectAll("path")
        .data((r: any) =>
          SERIES.map((s) => ({
            s,
            v: r[s.key] as number | null,
            mm: r.date,
          })).filter((p) => p.v != null),
        )
        .join("path")
        .attr("d", (p: any) =>
          topRoundedRectPath(
            x1(p.s.key as string) as number,
            y(p.v as number),
            x1.bandwidth(),
            y(0) - y(p.v as number),
            4,
          ),
        )
        .attr("fill", (p: any) => p.s.color)
        .on("mousemove", (ev: MouseEvent, p: any) =>
          tip.show(`<b>${p.mm}</b><br>${p.s.label}: <b>${p.v}</b>`, ev),
        )
        .on("mouseleave", () => tip.hide());
    },
    [rows, height],
  );
  const { ref, svgRef, width } = useD3Chart(draw);

  return (
    <div
      ref={ref}
      style={{ position: "relative", width: "100%", fontFamily: FONT }}
    >
      <svg ref={svgRef} width={width} height={height} />
    </div>
  );
}
